import React, { Suspense, useState, useEffect, Fragment, lazy } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { createTheme, ThemeProvider } from "@mui/material/styles";

import VerticalLayout from "./Layouts/VerticalLayout/";
import ScannerHomeGate from "./handheld/ScannerHomeGate";
import HandheldIdleLogout from "./handheld/HandheldIdleLogout";
import HandheldPinGate from "./handheld/HandheldPinGate";
import {
  handheldPinLoginEnv,
  shouldUseHandheldPinLogin,
} from "../helpers/handheldPin";
import NonAuthLayout from "./Layouts/NonAuthLayout";
import ErrorBoundary from "./ErrorBoundary";
// import AppRoute from "../routes/routes";
import { authProtectedRoutes, publicRoutes } from "../routes/";
import { requestCurrentUser } from "../actions/user";
import "../assets/scss/theme.scss";
import PreLoader from "./common/PreLoader";
// import useTheme from "../helpers/useTheme";
import { requestTheme } from "../actions/settings";
import { requestPricings } from "../actions/pricings";
import { setGlobalAlert } from "../actions/common";
import useColorMode from "../helpers/useColorMode";
import Snackbar from "./common/Snackbar";
import CancelledItemDialog from "./common/CancelledItemDialog";
import AllInitialApiCallsComponent from "./common/AllInitialApiCallsComponent";
import BulkIgnoreJobWatcher from "../pages/listings/Listings/BulkIgnoreJobWatcher";
import MoveAllToAppLocationJobWatcher from "../pages/listings/Listings/MoveAllToAppLocationJobWatcher";
import BulkSendToChannelJobWatcher from "../pages/LocalInventory/AllAddedBlanks/ListAllAddedBlanks/BulkSendToChannelJobWatcher";
import BulkJobsTracker from "./common/BulkJobs/BulkJobsTracker";
import ProductCreateJobWatcher from "./ProductCreateJobWatcher";
import ChannelPublishWatcher from "./ChannelPublishWatcher";
import MerchDropJobWatcher from "./MerchDropJobWatcher";
import BatchSaveWatcher from "./BatchSaveWatcher";
import { CONSUMER_KEY } from "../constants/common";
import { getToken, removeToken } from "../helpers/localStorageCache";
import {
  devBigFrogSettingsPath,
  isDevBigFrogMockSession,
} from "../helpers/devAutoLogin";
import { applyLoaderBrand, applyTenantBrand } from "../helpers/brandColor";
import { applyTenantFavicon } from "../helpers/favicon";
import { isRiverrDomain } from "../utils";
import RiverrAdsPixel from "./marketing/RiverrAdsPixel";
import AccountsClarity from "./AccountsClarity";

const ShopifyUser = lazy(() => import("../pages/redirects/ShopifyUser"));
const Pages404 = lazy(() => import("../pages/static/404"));

// Heavy context providers, split out of the entry bundle. `AdminProviders`
// (Apollo + Polaris + Polaris CSS) wraps the admin shell; `ApolloBoundary`
// (Apollo only) wraps the public auth pages + Shopify redirect. Standalone
// routes (the full-screen customizer) render inside neither, so they never
// pull Apollo/Polaris. See providers/AdminProviders.js and index.js.
const AdminProviders = lazy(() => import("./providers/AdminProviders"));
const ApolloBoundary = lazy(() => import("./providers/ApolloBoundary"));

// Routes that should render instantly without waiting on the
// boot-time API trio (currentUser/theme/pricings) or showing the
// chase-dot PreLoader for lazy chunk loads. The onboarding wizard
// owns its own auth + state lifecycle and the persisted-record
// hydrate happens inside each step's render path (form mounts
// with EMPTY defaults, populates when hydrate arrives) — so the
// global app-shell loaders are pure latency for these pages.
//
// Initial entry: read once at module load so the first paint isn't
// blocked even before React commits. Subsequent navigations are
// handled by the Suspense fallback below, which re-reads pathname
// each time it mounts.
const isInstantPath = (pathname) =>
  pathname.startsWith("/onboarding") || pathname === "/seller-session";
const initialPathIsInstant = isInstantPath(window.location.pathname);
const initialPathIsSellerSession =
  window.location.pathname === "/seller-session";

const SuspenseFallback = () =>
  isInstantPath(window.location.pathname) ? null : <PreLoader />;

const App = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const globalAlert = useSelector((state) => state?.common?.alert);
  const primaryColor = useSelector(
    (state) => state?.settings?.themeData?.primaryColor
  );

  // Resolved colour mode ("light" | "dark") drives the MUI palette so
  // MUI surfaces (Dialog, Menu, Paper, Select) follow the same dark
  // scheme as the token-driven chrome. Re-renders when the user flips
  // the topbar toggle or the OS scheme changes (system preference).
  const { mode: colorMode } = useColorMode();

  // Tenant favicon from Enterprise Branding ("Logo symbol / favicon").
  // Runs from the app shell so login and dashboard pages both get it.
  // Object identity only changes when the theme reloads, so this doesn't
  // re-run on unrelated renders.
  const logoSymbol = useSelector(
    (state) => state?.settings?.themeData?.logoSymbol
  );
  const legacyFavicon = useSelector(
    (state) => state?.settings?.themeData?.favicon
  );
  useEffect(() => {
    applyTenantFavicon({ logoSymbol, favicon: legacyFavicon });
  }, [logoSymbol, legacyFavicon]);

  // River loader + chrome brand: apply as soon as theme resolves (during
  // boot PreLoader), not after VerticalLayout mounts. Enterprise domains stay
  // neutral until primaryColor arrives; app.riverr.app gets the house gradient
  // immediately because the domain is known at boot.
  useEffect(() => {
    if (isRiverrDomain) {
      applyLoaderBrand(null, { isRiverrDomain: true });
    }
  }, []);

  useEffect(() => {
    if (!primaryColor) return;
    applyTenantBrand(primaryColor);
    if (!isRiverrDomain) {
      applyLoaderBrand(primaryColor);
    }
  }, [primaryColor]);

  useEffect(() => {
    if (initialPathIsSellerSession) {
      removeToken("bgProcessingState");
      return;
    }

    if (!initialPathIsInstant) setLoading(true);

    Promise.allSettled([
      dispatch(requestCurrentUser()),
      dispatch(requestTheme({ consumer_key: CONSUMER_KEY })),
      dispatch(requestPricings()),
    ]).finally(() => setLoading(false));

    removeToken("bgProcessingState");
  }, [dispatch]);

  useEffect(() => {
    if (!isDevBigFrogMockSession()) return;
    const { pathname } = location;
    if (pathname === "/login" || pathname === "/") {
      navigate(devBigFrogSettingsPath, { replace: true });
    }
  }, [location.pathname, navigate]);

  if (loading) {
    return <PreLoader />;
  }
  const theme = createTheme({
    // Palette mode mirrors the app's colour-mode preference. In dark
    // mode the background/text/divider values are pinned to the same
    // warm-neutral dark tokens used by the SCSS chrome
    // (`_design-tokens.scss :root[data-theme="dark"]`) so MUI surfaces
    // don't drift to MUI's default cool-grey dark palette.
    palette: {
      mode: colorMode,
      ...(colorMode === "dark"
        ? {
            background: { default: "#161513", paper: "#201f1c" },
            text: { primary: "#f3f1ec", secondary: "#b4ada0" },
            divider: "#322f2a",
          }
        : {}),
    },
    typography: {
      fontFamily: ["Vastago Grotesk", "sans-serif"].join(","),
      h5: {
        fontFamily: ["Vastago Grotesk", "sans-serif"].join(","), // Specific to h5
      },
      h6: {
        fontFamily: ["Vastago Grotesk", "sans-serif"].join(","), // Specific to h6
      },
      body1: {
        fontFamily: ["Vastago Grotesk", "sans-serif"].join(","), // Specific to body
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <Fragment>
        {!initialPathIsSellerSession && <AllInitialApiCallsComponent />}

        <Suspense fallback={<SuspenseFallback />}>
          <Routes>
            {publicRoutes.map((route, idx) => (
              <Route
                key={idx}
                path={route.path}
                element={
                  route.standalone ? (
                    <div className="standalone-editor">
                      <ErrorBoundary>
                        <route.component />
                      </ErrorBoundary>
                    </div>
                  ) : (
                    <ApolloBoundary>
                      <NonAuthLayout>
                        <route.component />
                      </NonAuthLayout>
                    </ApolloBoundary>
                  )
                }
                exact={route.exact}
              />
            ))}

            {authProtectedRoutes.map((route, idx) => (
              <Route
                key={idx}
                path={route.path}
                element={
                  !getToken("authUser") ? (
                    shouldUseHandheldPinLogin({
                      ...handheldPinLoginEnv(),
                      pathname: location.pathname,
                      search: location.search,
                    }) ? (
                      <HandheldPinGate asLogin />
                    ) : location.pathname.startsWith("/apply") ? (
                      // Seller application links (e.g. the verification
                      // email) come back here after sign-in.
                      <Navigate
                        to={`/login?redirectTo=${encodeURIComponent(
                          `${location.pathname}${location.search || ""}`
                        )}`}
                      />
                    ) : (
                      <Navigate to="/login" />
                    )
                  ) : route.standalone ? (
                    // Full-screen editors (customizer / sticker / necklace /
                    // nester) render without the admin chrome and without
                    // Apollo/Polaris — they use neither. The `standalone-editor`
                    // class zeroes the `.page-content` topbar offset for the
                    // editors that use that class (necklace/nester); the
                    // overlay editors (customizer/sticker) ignore it.
                    //
                    // `withAdminProviders`: POS-hosted admin modules (Quotes)
                    // need Apollo/Polaris but not VerticalLayout chrome.
                    route.withAdminProviders ? (
                      <div className="standalone-editor">
                        <AdminProviders>
                          <ErrorBoundary>
                            <route.component />
                          </ErrorBoundary>
                        </AdminProviders>
                      </div>
                    ) : (
                      <div className="standalone-editor">
                        <ErrorBoundary>
                          <route.component />
                        </ErrorBoundary>
                      </div>
                    )
                  ) : (
                    <AdminProviders>
                      <ScannerHomeGate />
                      <HandheldIdleLogout />
                      <HandheldPinGate />
                      <VerticalLayout>
                        <route.component />
                      </VerticalLayout>
                    </AdminProviders>
                  )
                }
                exact={route.exact}
              />
            ))}

            <Route
              path="shopify-user"
              exact
              element={
                <ApolloBoundary>
                  <ShopifyUser />
                </ApolloBoundary>
              }
            />
            <Route
              path="app-user"
              exact
              element={
                <ApolloBoundary>
                  <ShopifyUser />
                </ApolloBoundary>
              }
            />
            <Route path="*" element={<Pages404 />} />
          </Routes>
        </Suspense>

        {!isEmpty(globalAlert) && (
          <Snackbar
            alert={globalAlert}
            onClose={() => dispatch(setGlobalAlert({}))}
            open={!isEmpty(globalAlert)}
          />
        )}

        <CancelledItemDialog />

        <RiverrAdsPixel />
        <AccountsClarity />
        <BulkIgnoreJobWatcher />
        <MoveAllToAppLocationJobWatcher />
        <BulkSendToChannelJobWatcher />
        <BulkJobsTracker />
        <ProductCreateJobWatcher />
        <ChannelPublishWatcher />
        <MerchDropJobWatcher />
        <BatchSaveWatcher />
      </Fragment>
    </ThemeProvider>
  );
};

export default App;
